import { useState } from "react";
import { Modal, TextInput, Button, Group, Alert, Select, Checkbox } from "@mantine/core";
import { api } from "../services/api";

interface Family {
  id: number;
  family_name: string;
  unique_link: string;
  dietary_restrictions: string | null;
  guests: any[];
}

interface AddGuestModalProps {
  opened: boolean;
  onClose: () => void;
  familyId: number | null;
  families: Family[];
  onSuccess: () => void;
}

export function AddGuestModal({ opened, onClose, familyId, families, onSuccess }: AddGuestModalProps) {
  const [selectedFamilyId, setSelectedFamilyId] = useState<string>("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [isChild, setIsChild] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const targetFamilyId = familyId || parseInt(selectedFamilyId);
    
    if (!targetFamilyId || !firstName.trim()) {
      setError("Family and first name are required");
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      await api.createGuest(targetFamilyId, firstName.trim(), lastName.trim(), isChild);
      setSelectedFamilyId("");
      setFirstName("");
      setLastName("");
      setIsChild(false);
      onSuccess();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create guest. Please try again.");
      console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setSelectedFamilyId("");
    setFirstName("");
    setLastName("");
    setIsChild(false);
    setError(null);
    onClose();
  };

  const familyOptions = families.map(family => ({
    value: family.id.toString(),
    label: family.family_name
  }));

  return (
    <Modal 
      opened={opened} 
      onClose={handleClose} 
      title="Add New Guest" 
      size="md"
      centered
    >
      <form onSubmit={handleSubmit}>
        {error && (
          <Alert color="red" style={{ marginBottom: "1rem" }}>
            {error}
          </Alert>
        )}

        {!familyId && (
          <Select
            label="Family"
            placeholder="Select a family"
            data={familyOptions}
            value={selectedFamilyId}
            onChange={(value) => setSelectedFamilyId(value || "")}
            required
            searchable
            style={{ marginBottom: "1rem" }}
          />
        )}

        <TextInput
          label="First Name"
          placeholder="e.g., John"
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
          required
          style={{ marginBottom: "1rem" }}
        />

        <TextInput
          label="Last Name (optional)"
          placeholder="e.g., Smith"
          value={lastName}
          onChange={(e) => setLastName(e.target.value)}
          style={{ marginBottom: "1rem" }}
        />

        <Checkbox
          label="Child?"
          checked={isChild}
          onChange={(e) => setIsChild(e.currentTarget.checked)}
          style={{ marginBottom: "1.5rem" }}
        />

        <Group justify="flex-end">
          <Button variant="subtle" onClick={handleClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? "Adding..." : "Add Guest"}
          </Button>
        </Group>
      </form>
    </Modal>
  );
}
